import React from "react";
import ScrollJourney from "../components/ScrollJourney";
import ContactSection from "../components/ContactSection";
import { useReveal } from "../hooks/useReveal";
import { SECURITY_CHAPTERS, SECURITY_SERVICES, SECURITY_APPROACH, IMAGES } from "../mock";
import { ArrowRight, ShieldCheck } from "lucide-react";

const scrollToContact = () => {
  const el = document.getElementById("contact");
  if (el) el.scrollIntoView({ behavior: "smooth" });
};

function ServicesSection() {
  const [ref, visible] = useReveal();
  return (
    <section className="bg-[#f6f1e7] py-24 md:py-32">
      <div className="mx-auto max-w-[1400px] px-6 md:px-10">
        <div ref={ref} className={`reveal ${visible ? "in-view" : ""}`}>
          <p className="text-[#7c5230] text-xs tracking-[0.25em] uppercase mb-6">01 · Services</p>
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
            <h2 className="font-display text-4xl md:text-6xl lg:text-7xl font-light leading-[1.0] text-[#10271a] max-w-2xl">
              Protection, <em className="italic text-[#1f4a33]">by design.</em>
            </h2>
            <p className="text-[#5e6b61] text-lg max-w-sm">
              A full-spectrum security capability — from the first risk assessment to boots on the ground.
            </p>
          </div>
        </div>

        <div className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-8">
          {SECURITY_SERVICES.map((s, i) => (
            <ServiceCard key={s.no} service={s} delay={(i % 2) * 0.1} />
          ))}
        </div>
      </div>
    </section>
  );
}

function ServiceCard({ service, delay }) {
  const [ref, visible] = useReveal();
  return (
    <div
      ref={ref}
      className={`group bg-[#fbf8f1] rounded-3xl p-9 md:p-10 border border-[#10271a]/5 hover:border-[#1f4a33]/30 shadow-[0_18px_40px_-30px_rgba(16,39,26,0.5)] transition-all duration-500 hover:-translate-y-1 reveal ${visible ? "in-view" : ""}`}
      style={{ animationDelay: `${delay}s` }}
    >
      <div className="flex items-start justify-between">
        <span className="h-12 w-12 rounded-xl bg-[#1f4a33]/10 text-[#1f4a33] flex items-center justify-center">
          <ShieldCheck size={22} />
        </span>
        <span className="font-display text-4xl text-[#10271a]/15">{service.no}</span>
      </div>
      <h3 className="font-display text-3xl text-[#10271a] mt-7">{service.name}</h3>
      <p className="mt-4 text-[#5e6b61] leading-relaxed">{service.description}</p>
      <button
        onClick={scrollToContact}
        className="mt-7 group/btn inline-flex items-center gap-2.5 text-[#1f4a33] text-[13px] tracking-[0.1em] uppercase font-semibold border-b-2 border-[#1f4a33]/30 pb-1 hover:border-[#1f4a33] transition-colors"
      >
        Contact for more information
        <ArrowRight size={16} className="transition-transform group-hover/btn:translate-x-1" />
      </button>
    </div>
  );
}

function ApproachSection() {
  const [ref, visible] = useReveal();
  return (
    <section className="relative bg-[#10271a] text-[#f6f1e7] overflow-hidden grain">
      <img src={IMAGES.security.gear} alt="Security readiness" className="absolute inset-0 h-full w-full object-cover opacity-20" />
      <div className="relative mx-auto max-w-[1400px] px-6 md:px-10 py-24 md:py-32">
        <div ref={ref} className={`reveal ${visible ? "in-view" : ""}`}>
          <p className="text-[#b08a5f] text-xs tracking-[0.25em] uppercase mb-6">02 · Our approach</p>
          <h2 className="font-display text-4xl md:text-6xl lg:text-7xl font-light leading-[1.0] max-w-3xl">
            Quiet competence, <em className="italic text-[#e9d9bf]">every time.</em>
          </h2>
          <p className="mt-7 text-[#f6f1e7]/75 text-lg max-w-xl leading-relaxed">
            We combine disciplined, vetted personnel with intelligence-led planning and the right equipment — so our clients can operate, travel and build without fear.
          </p>
        </div>

        <div className="mt-16 grid grid-cols-1 sm:grid-cols-3 gap-10">
          {SECURITY_APPROACH.map((a, i) => (
            <Stat key={a.stat} stat={a.stat} label={a.label} delay={i * 0.1} />
          ))}
        </div>
      </div>
    </section>
  );
}

function Stat({ stat, label, delay }) {
  const [ref, visible] = useReveal();
  return (
    <div ref={ref} className={`reveal ${visible ? "in-view" : ""}`} style={{ animationDelay: `${delay}s` }}>
      <p className="font-display text-6xl md:text-7xl text-[#e9d9bf] font-light leading-none">{stat}</p>
      <p className="mt-4 text-[#f6f1e7]/70 leading-relaxed max-w-[16rem]">{label}</p>
    </div>
  );
}

function CtaBand() {
  const [ref, visible] = useReveal();
  return (
    <section className="bg-[#1f4a33] text-[#f6f1e7]">
      <div className="mx-auto max-w-[1400px] px-6 md:px-10 py-24 md:py-32">
        <div ref={ref} className={`grid grid-cols-1 lg:grid-cols-2 gap-10 lg:items-end reveal ${visible ? "in-view" : ""}`}>
          <div>
            <p className="text-[#b08a5f] text-xs tracking-[0.25em] uppercase mb-6">03 · Engage us</p>
            <h2 className="font-display text-4xl md:text-6xl lg:text-7xl font-light leading-[1.0]">
              Secure what <em className="italic text-[#e9d9bf]">matters.</em>
            </h2>
            <p className="mt-7 text-[#f6f1e7]/75 text-lg max-w-md leading-relaxed">
              Tell us about your people, assets or operation and we'll design a protective solution around them.
            </p>
          </div>
          <div className="lg:justify-self-end">
            <button
              onClick={scrollToContact}
              className="group inline-flex items-center gap-3 bg-[#f6f1e7] text-[#10271a] px-8 py-4 rounded-full text-sm tracking-[0.12em] uppercase font-semibold hover:bg-white transition-colors"
            >
              Contact for more information
              <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
            </button>
            <p className="mt-4 text-[#f6f1e7]/55 text-sm">Confidential · Professional · Accountable</p>
          </div>
        </div>
      </div>
    </section>
  );
}

export default function SecurityPage() {
  return (
    <main>
      <ScrollJourney chapters={SECURITY_CHAPTERS} progressLabel="Follow the operation" ambience="ops" />
      <ServicesSection />
      <ApproachSection />
      <CtaBand />
      <ContactSection
        eyebrow="Engage our team"
        heading="Request a confidential"
        emphasis="consultation."
        blurb="Executives, organisations and families — share your requirement and our operations desk will respond discreetly."
      />
    </main>
  );
}
